import { useMemo } from 'react'
import { toast } from 'sonner'
import { useConfirm } from '@/components/common/ConfirmDialog'
import {
  applyClientSideTableState,
  DataTable,
  useTableState,
  type ColumnDef,
} from '@/components/data-table'
import { BoolIcon } from '@/components/common/BoolIcon'
import { AddButton } from '@/components/common/AddButton'
import { useDeleteField, useUpdateSectionLayout } from '../hooks'
import type { FieldDefinition, FormSchema, FormSection } from '../types'
import { widthLabel } from './widthLabel'
import { useAddPresentationalField } from './useAddPresentationalField'

type Props = {
  schema: FormSchema
  section: FormSection
  objectType: string
  onAddField: () => void
  onEditField: (field: FieldDefinition) => void
}

export function SectionEditorPanel({ schema, section, objectType, onAddField, onEditField }: Props) {
  const confirm = useConfirm()
  const del = useDeleteField(objectType)
  const updateLayout = useUpdateSectionLayout(objectType)
  const addPresent = useAddPresentationalField(objectType)
  const tableId = `section-fields-${objectType}-${section.code}`
  const { state, setSort, setSearch, setFilter, removeFilter, clearFilters } = useTableState(tableId)

  // Derive fields in this section from placements (N:M - field may appear in multiple sections).
  const fields = useMemo(() => {
    const fieldById = new Map(schema.fields.map(f => [f.id, f]))
    return (schema.placements ?? [])
      .filter(p => p.sectionId === section.id)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map(p => {
        const field = fieldById.get(p.fieldId)
        return field ? { ...field, _colWidth: p.colWidth } : null
      })
      .filter((x): x is FieldDefinition & { _colWidth: number } => x !== null)
  }, [schema.fields, schema.placements, section.id])

  const columns: ColumnDef<FieldDefinition>[] = useMemo(
    () => [
      {
        key: 'fieldCode',
        header: 'Kod pola',
        sortable: true,
        filter: { kind: 'text', placeholder: 'Filtruj kod...' },
        accessor: (f) => <span className="font-mono text-xs">{f.fieldCode}</span>,
        alwaysVisible: true,
      },
      {
        key: 'labelPl',
        header: 'Etykieta',
        sortable: true,
        filter: { kind: 'text', placeholder: 'Filtruj label...' },
        accessor: (f) => f.labelPl,
        alwaysVisible: true,
      },
      {
        key: 'fieldType',
        header: 'Typ',
        sortable: true,
        filter: { kind: 'text' },
        accessor: (f) => (
          <span className="rounded bg-slate-200 text-slate-700 px-2 py-0.5 text-xs">{f.fieldType}</span>
        ),
        className: 'whitespace-nowrap',
      },
      {
        key: 'isRequired',
        header: 'Wymagane',
        sortable: true,
        filter: { kind: 'bool', trueLabel: 'Wymagane', falseLabel: 'Opcjonalne' },
        accessor: (f) => <div className="flex justify-center"><BoolIcon value={f.isRequired} /></div>,
        align: 'center',
        className: 'w-24',
      },
      {
        key: 'isActive',
        header: 'Aktywne',
        sortable: true,
        filter: { kind: 'bool', trueLabel: 'Aktywne', falseLabel: 'Nieaktywne' },
        accessor: (f) => <div className="flex justify-center"><BoolIcon value={f.isActive} /></div>,
        align: 'center',
        className: 'w-24',
      },
      {
        key: 'colWidth',
        header: 'Szerokość',
        sortable: true,
        accessor: (f) => widthLabel((f as FieldDefinition & { _colWidth?: number })._colWidth),
        align: 'center',
        className: 'w-20',
      },
      {
        key: 'actions',
        header: '',
        accessor: (f) => (
          <div className="text-right whitespace-nowrap">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onEditField(f)
              }}
              className="text-xs text-slate-600 hover:text-slate-900 px-1"
            >
              Edytuj
            </button>
            <button
              type="button"
              onClick={async (e) => {
                e.stopPropagation()
                const ok = await confirm({
                  title: `Usunąć pole ${f.fieldCode}?`,
                  description: 'Twarde usunięcie z field_definition. Operacja jest nieodwracalna.',
                  confirmLabel: 'Usuń',
                  variant: 'destructive',
                })
                if (ok) del.mutate(f.fieldCode, { onError: (err) => toast.error(formatError(err)) })
              }}
              className="text-xs text-red-600 hover:text-red-800 px-1 ml-2"
            >
              Usuń
            </button>
          </div>
        ),
        className: 'w-32',
        alwaysVisible: true,
      },
    ],
    [del, confirm, onEditField],
  )

  const filteredRows = useMemo(
    () =>
      applyClientSideTableState(fields, columns, state, {
        valueGetters: {
          fieldCode: (f) => f.fieldCode,
          labelPl: (f) => f.labelPl,
          fieldType: (f) => f.fieldType,
          isRequired: (f) => f.isRequired,
          isActive: (f) => f.isActive,
        },
        searchString: (f) => `${f.fieldCode} ${f.labelPl} ${f.fieldType}`.toLowerCase(),
      }),
    [fields, columns, state],
  )

  return (
    <main className="flex-1 flex flex-col overflow-hidden">
      <header className="px-4 py-3 border-b flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">{section.labelPl}</h2>
          <p className="text-xs text-slate-500 font-mono">{section.code}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => addPresent.add('SEPARATOR', section)}
            disabled={addPresent.isPending}
            className="rounded border border-slate-300 text-slate-700 px-2 py-1.5 text-xs whitespace-nowrap hover:bg-slate-50 disabled:opacity-50"
            title="Wstaw linię oddzielającą grupę pól"
          >
            + Separator
          </button>
          <button
            type="button"
            onClick={() => addPresent.add('SPACER', section)}
            disabled={addPresent.isPending}
            className="rounded border border-slate-300 text-slate-700 px-2 py-1.5 text-xs whitespace-nowrap hover:bg-slate-50 disabled:opacity-50"
            title="Wstaw puste miejsce wypełniające kolumny"
          >
            + Pusta przestrzeń
          </button>
          <button
            type="button"
            onClick={() => addPresent.add('INSTRUCTION', section)}
            disabled={addPresent.isPending}
            className="rounded border border-slate-300 text-slate-700 px-2 py-1.5 text-xs whitespace-nowrap hover:bg-slate-50 disabled:opacity-50"
            title="Wstaw info-box z instrukcją dla osoby wypełniającej formularz"
          >
            + Instrukcja
          </button>
          <button
            type="button"
            onClick={() => addPresent.add('HEADER', section)}
            disabled={addPresent.isPending}
            className="rounded border border-slate-300 text-slate-700 px-2 py-1.5 text-xs whitespace-nowrap hover:bg-slate-50 disabled:opacity-50"
            title="Wstaw nagłówek grupujący kolejne pola w sekcji"
          >
            + Nagłówek
          </button>
          <AddButton onClick={onAddField}>Dodaj pole</AddButton>
        </div>
      </header>

      <div className="flex-1 overflow-auto px-4 py-3">
        <DataTable<FieldDefinition>
          tableId={tableId}
          data={filteredRows}
          columns={columns}
          rowKey={(f) => f.fieldCode}
          sort={state.sort}
          onSortChange={setSort}
          state={state}
          searchable
          searchPlaceholder="Szukaj kodu / labela / typu..."
          onSearchChange={setSearch}
          onFilterChange={setFilter}
          onFilterRemove={removeFilter}
          onClearAll={clearFilters}
          emptyState={'Brak pól w tej sekcji. Kliknij "Dodaj pole".'}
          dragMode={{
            onReorder: (_keys, orderedRows) => {
              const entries = orderedRows.map((f, i) => ({
                fieldCode: f.fieldCode,
                sortOrder: i * 10,
                colWidth: (f as FieldDefinition & { _colWidth?: number })._colWidth ?? 12,
              }))
              updateLayout.mutate({ sectionCode: section.code, entries })
            },
          }}
        />
      </div>
    </main>
  )
}

function formatError(err: unknown): string {
  const ax = err as { response?: { data?: { detail?: string; title?: string } } }
  return ax.response?.data?.detail ?? ax.response?.data?.title ?? 'Błąd usuwania'
}
